// DOM-byggere. Ren TS, ingen rammeverk — samme mal som apps/cop og apps/prosjekt.
//
// REGELEN SOM HOLDER HELE KLIENTEN: her finnes ingen `innerHTML`. All tekst
// settes med `textContent`, all struktur med `createElement`. Grunnloven §4 sier
// det generelt («all feltavledet tekst escapes før DOM»), men på en sensorflate
// er det helt konkret: sensornavn, broens merknader og klassifiseringer kommer
// fra driftskonfigurasjon og fra en bro vi ikke skriver selv. Alt det er
// feltdata. `src/ingen-innerhtml.test.ts` — både her og i appene som bruker
// pakken — håndhever regelen på hele src/, også i filene som ennå ikke er
// skrevet.
import { somIkon } from './ikoner.js';
export function el(tag, cls = '', tekst) {
    const e = document.createElement(tag);
    if (cls)
        e.className = cls;
    if (tekst !== undefined)
        e.textContent = tekst;
    return e;
}
export const div = (cls = '', tekst) => el('div', cls, tekst);
export const span = (cls = '', tekst) => el('span', cls, tekst);
export const p = (cls = '', tekst) => el('p', cls, tekst);
export const h = (nivå, cls = '', tekst) => el(`h${nivå}`, cls, tekst);
/** Tømmer en node uten `innerHTML = ''`. */
export function tøm(node) {
    while (node.firstChild)
        node.removeChild(node.firstChild);
}
/** Bytt hele innholdet i en node. */
export function sett(node, ...barn) {
    tøm(node);
    for (const b of barn)
        if (b)
            node.appendChild(b);
}
export function knapp(tekst, påKlikk, valg = {}) {
    const b = el('button', `pk pk-${valg.variant ?? 'stille'}`);
    b.type = valg.type ?? 'button';
    if (valg.ikon)
        b.appendChild(valg.ikon);
    if (tekst)
        b.appendChild(span('pk-tekst', tekst));
    // Tittel bare når den sier noe MER enn etiketten — en tooltip som gjentar
    // knappeteksten er støy for alle og et dobbelt opplest ord for skjermleseren.
    if (valg.tittel && valg.tittel !== tekst)
        b.title = valg.tittel;
    if (!tekst && valg.tittel)
        b.setAttribute('aria-label', valg.tittel);
    if (valg.deaktivert)
        b.disabled = true;
    if (påKlikk)
        b.addEventListener('click', påKlikk);
    return b;
}
/** Liten merkelapp: status, sensorart, tilhørighet. */
export function merke(tekst, variant = '') {
    return span(`merke${variant ? ` merke-${variant}` : ''}`, tekst);
}
/**
 * Nøkkel/verdi-rad. Verdien får monospace + tabulære sifre, fordi radene på en
 * sensorflate nesten alltid er tall som skal kunne sammenlignes loddrett
 * (peilinger, avstander, frekvenser). Proporsjonale sifre gjør en kolonne med
 * tall til en ujevn kant, og da leses den ikke som en kolonne.
 */
export function nv(nøkkel, verdi, variant = '') {
    const rad = div(`nv${variant ? ` nv-${variant}` : ''}`);
    rad.append(span('nv-k', nøkkel), span('nv-v', verdi));
    return rad;
}
/**
 * Statusprikk MED tekst. Aldri farge alene: posten betjenes i mørket, ofte med
 * rødt lys eller NVG, og da forsvinner fargeforskjeller helt. Prikken er en
 * hurtiglesing for den som allerede vet; teksten er sannheten.
 */
export function statusprikk(tekst, variant) {
    const rot = span(`status status-${variant}`);
    rot.append(span('status-prikk'), span('status-tekst', tekst));
    return rot;
}
/** Etikett over en verdi — grunnenheten i sensorstolpen og kontaktkortet. */
export function felt(etikett, verdi, variant = '') {
    const rot = div(`vfelt${variant ? ` vfelt-${variant}` : ''}`);
    rot.append(span('vfelt-etikett', etikett), span('vfelt-verdi', verdi));
    return rot;
}
/** Bryter (avkrysning) med etikett. Brukes til lag-av/på og øvingsbryteren. */
export function bryter(etikett, på, endret) {
    const rot = el('label', 'bryter');
    const inn = el('input');
    inn.type = 'checkbox';
    inn.checked = på;
    inn.addEventListener('change', () => endret(inn.checked));
    rot.append(inn, span('bryter-tekst', etikett));
    return rot;
}
/**
 * Kopier-knapp for en verdi som skal ut av skjermen (et MGRS-grid, en
 * begrunnelse). Utklippstavla kan være blokkert — usikker opprinnelse, avslått
 * rettighet, eller en nettleser uten `navigator.clipboard`. Da SIER knappen det,
 * framfor å se vellykket ut mens ingenting ble kopiert: verdien står synlig ved
 * siden av og kan merkes for hånd. Et grid som operatøren tror er kopiert, men
 * ikke er det, er en feil noen andre oppdager først.
 */
export function kopierknapp(verdi, hva, si) {
    return knapp('Kopiér', () => {
        const skriv = globalThis.navigator?.clipboard?.writeText;
        if (!skriv) {
            si('Nettleseren tillot ikke kopiering — merk teksten og kopiér for hånd.', 'feil');
            return;
        }
        skriv.call(globalThis.navigator.clipboard, verdi)
            .then(() => si(`${hva} er kopiert`))
            .catch(() => si('Kopiering ble avvist — merk teksten og kopiér for hånd.', 'feil'));
    }, { variant: 'stille' });
}
/** Ikon + tekst på én linje. Brukes i tomtilstander og advarsler. */
export function ikonlinje(navn, tekst, cls = 'ikonlinje') {
    const rot = div(cls);
    rot.append(somIkon(navn), span('', tekst));
    return rot;
}
//# sourceMappingURL=dom.js.map